import { useState, useEffect } from 'react';
import Table from '../../components/Table';
import { Grid, IconButton, Tooltip } from '@mui/material';
import DialogBox from '../../components/DialogBox';
import EmailIcon from '@mui/icons-material/Email';
import UpdateAreaAdminUser from './UpdateAreaAdminUser';
import Loader from 'components/Loader';
import AddIcon from '@mui/icons-material/Add';
import SuccessError from 'components/SuccessError';
import CustomButton from 'components/Button';
import { useSelector, useDispatch } from 'react-redux';
import { authPostRequest, getAuthRequest } from 'Api/api';
import { isSuperAdmin, tableOptionFilter, isAdmin, isUser, isAreaAdmin } from 'utils/utils';
import CreateAreaAdminUser from './CreateAreaAdminUser';
import DeleteSpan from 'components/DeleteSpan';
import { addUsers } from 'reduxStore/authSlice';
import CRUDActions from 'components/CRUDActions';
import { ROLES } from 'constant/enums';
import StoreNameLabel from 'components/StoreNameLabel';
import TextEllipsize from 'utils/TextEllipsize';
import SuperAdminView from 'pages/adminsView/SuperAdminView';
import { useNavigate } from 'react-router-dom';
import { dateFormatterUTC } from 'utils/dateFormatter';

let storeNameArray = [];
const columnOptions = {
  setCellProps: () => ({ style: { maxWidth: '200px' } }),
  customBodyRender: (value) => <TextEllipsize value={value} length={20} />
};
const columns = [
  {
    name: 'firstName',
    label: 'First Name',
    options: {
      filter: true,
      sort: false,
      ...columnOptions
    }
  },

  {
    name: 'lastName',
    label: 'Last Name',
    options: {
      filter: true,
      sort: false,
      ...columnOptions
    }
  },

  {
    name: 'email',
    label: 'Email',
    options: {
      filter: true,
      sort: false,
      ...columnOptions
    }
  },

  {
    name: 'lastLogin',
    label: 'Last Login',
    options: {
      filter: true,
      sort: true,
      customBodyRender: (value) => dateFormatterUTC(value)
    }
  }
];

const AreaAdmins = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [addOpenModal, setAddOpenModal] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showAlertBar, setShowAlertBar] = useState(false);
  const [isFormSaved, setIsFormSaved] = useState(false);
  const [successMessage, setSuccessMessage] = useState([]);
  const [isDeleted, setIsDeleted] = useState(false);
  const [errorMessage, setErrorMessage] = useState([]);
  const [user, setUser] = useState();
  const [users, setUsers] = useState();
  const [storeNameList, setStoreNameList] = useState([]);
  const { userData, adminAccess } = useSelector((store) => store.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // const [filterBy, setFilterBy] = useState('None');

  //For Open Dialog for delete user
  const handleDialogOpen = (user) => {
    setUser(users[user]);
    setOpenDialog(true);
  };

  //For Open modal for update user
  const handleOpenModal = (user) => {
    setUser(users[user]);
    setOpenModal(true);
  };

  //Handle confirmation for delete
  const handleConfirm = async () => {
    try {
      const params = {
        userId: userData.userId,
        email: user.email,
        id: userData.userId
      };
      setShowLoader(true);
      setShowAlertBar(false);
      setIsDeleted(false);
      const delUser = await authPostRequest('deleteUser', params);
      setShowLoader(false);
      if (delUser.resStatus === 1) {
        setSuccessMessage([delUser.resMessage]);
        setIsDeleted(true);
      } else {
        setErrorMessage([delUser.resMessage]);
      }
      setIsFormSaved(true);

      setShowAlertBar(true);
      setOpenDialog(!openDialog);
      window.scrollTo(0, 0);
    } catch (err) {
      setErrorMessage(['Error']);
      window.scrollTo(0, 0);
    }
  };

  //state set from delete user
  const handleDialogState = () => {
    setOpenDialog(!openDialog);
  };

  const filterUsersList = (usersList) => {
    return usersList?.filter(
      (list) =>
        ROLES[list.role] === ROLES.ROLE_AREA_ADMIN || ROLES[list.role] === ROLES.ROLE_SUPER_ADMIN
    );
  };

  // getting list of store names only
  const filterStoreNameList = (usersList) => {
    return usersList
      ?.filter(
        (list) =>
          ROLES[list.role] === ROLES.ROLE_AREA_ADMIN || ROLES[list.role] === ROLES.ROLE_SUPER_ADMIN
      )
      .map((list) => list.storesList);
  };

  const getUsersList = async () => {
    try {
      setShowLoader(true);
      const usersList = await getAuthRequest('getUsersList');

      // filter user list
      setUsers(filterUsersList(usersList?.usersResponses));
      setStoreNameList(filterStoreNameList(usersList?.usersResponses));
      setShowLoader(false);
    } catch (err) {
      setShowLoader(false);
      console.log(err);
    }
  };

  useEffect(() => {
    if (isSuperAdmin(userData.roles)) {
      getUsersList(); // will return filter area admin list
    }
    if (isAdmin(userData?.roles) || isUser(userData?.roles) || isAreaAdmin(userData?.roles)) {
      if (adminAccess) {
        navigate('/Error');
      } else navigate('/403');
    }
  }, [isFormSaved, isDeleted]);

  useEffect(() => {
    dispatch(addUsers(users));
  }, [users]);

  const sendInvite = async (rowsDeleted) => {
    const user = users[rowsDeleted];

    try {
      const requestParams = {
        userId: userData.userId,
        email: user.email
      };
      setShowLoader(true);
      setShowAlertBar(false);
      const invite = await authPostRequest('resendInvite', requestParams);

      setShowLoader(false);

      if (invite.resStatus === 1) {
        setSuccessMessage([invite.resMessage]);
      } else {
        setErrorMessage([invite.resMessage]);
      }
      setShowAlertBar(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setErrorMessage([err]);
      window.scrollTo(0, 0);
    }
  };

  const getStoreNameFilterArray = () => {
    const storeNamesSet = new Set();

    const storeNameArrayWithEmptyStrings = storeNameList?.map((list) =>
      list
        .filter(({ storeName }) => !storeNamesSet.has(storeName) && storeNamesSet.add(storeName))
        .map((obj) => obj.storeName)
        .toString()
    );

    storeNameArray = storeNameArrayWithEmptyStrings?.filter(Boolean);
  };
  getStoreNameFilterArray();

  return (
    <>
      <SuperAdminView />
      <Grid display="flex" alignItems="center" justifyContent="flex-end">
        {showAlertBar && (
          <SuccessError
            successMessage={successMessage}
            errorMessage={errorMessage}
            setShowAlertBar={setShowAlertBar}
          />
        )}
        <CustomButton
          add={true}
          type="submit"
          endIcon={<AddIcon />}
          onClick={() => setAddOpenModal(true)}>
          Add
        </CustomButton>
      </Grid>

      <Table
        data={users}
        options={tableOptionFilter}
        columns={[
          ...columns,

          {
            name: 'role',
            label: 'Role',
            options: {
              filter: true,
              sort: true,
              customBodyRender: (value) => ROLES[value]
            }
          },
          {
            name: 'storesList',
            label: 'Store Name',
            options: {
              filter: true,
              sort: false,
              setCellProps: () => ({ style: { width: '60vh' } }),
              customFilterListOptions: {
                customFilterListRender: (v) => `${v}`
              },
              filterOptions: {
                names: storeNameArray,
                logic: (storesList, filters) => {
                  if (filters[0]?.length) {
                    const storeNameArray = storesList.map((list) => list.storeName);
                    return !filters[0].includes(storeNameArray.toString());
                  }
                  return false;
                }
              },
              customBodyRender: (storesList) =>
                storesList.map((list, index) => {
                  let isLastIndex = null;
                  if (index !== storesList.length - 1) {
                    isLastIndex = true;
                  } else {
                    isLastIndex = false;
                  }
                  return (
                    <StoreNameLabel
                      key={index}
                      storeName={list.storeName}
                      lastIndex={isLastIndex}
                      active={list.active}
                    />
                  );
                })
            }
          },
          {
            name: 'actions',
            label: 'Actions',
            options: {
              filter: false,
              sort: false,

              //Actions Delete, Update, Resend Invite
              customBodyRender: (value, tableMeta) => {
                const rowEmail = tableMeta?.rowData?.[2];
                const rowUser = users?.find((u) => u?.email === rowEmail);
                const isSelf = rowEmail === userData?.userName;
                const isInactive = rowUser ? !rowUser.active : false;
                return (
                  <Grid sx={{ ml: '-1.5vh' }} display="flex">
                    <CRUDActions
                      showDelete={!isSelf}
                      showEdit={!isSelf}
                      onDelete={() => handleDialogOpen(tableMeta.rowIndex)}
                      onEdit={() => handleOpenModal(tableMeta.rowIndex)}
                    />

                    {isInactive && (
                      <Tooltip title="Resend invite">
                        <IconButton onClick={() => sendInvite(tableMeta.rowIndex)}>
                          <EmailIcon style={{ color: '#040273' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Grid>
                );
              }
            }
          }
        ]}
      />

      {showLoader && <Loader open={showLoader} />}

      {openDialog && (
        <DialogBox
          handleDialogBoxState={handleDialogState}
          openDialogBox={openDialog}
          handleConfirmAction={handleConfirm}
          deleteMessage={<DeleteSpan message="user associated with email" toBeDel={user.email} />}
        />
      )}

      {openModal && (
        <UpdateAreaAdminUser
          openModal={openModal}
          setOpenModal={setOpenModal}
          user={user}
          setSuccess={setSuccessMessage}
          setAlertBar={setShowAlertBar}
          setIsFormSaved={setIsFormSaved}
          isFormSaved={isFormSaved}
        />
      )}

      {addOpenModal && (
        <CreateAreaAdminUser
          addOpenModal={addOpenModal}
          setAddOpenModal={setAddOpenModal}
          setSuccess={setSuccessMessage}
          setAlertBar={setShowAlertBar}
          setIsFormSaved={setIsFormSaved}
          isFormSaved={isFormSaved}
        />
      )}
    </>
  );
};

export default AreaAdmins;
